/**
 * Wedding vendors on the logistics questionnaire, and the follow-ups we send them after the wedding (Traci's Loom,
 * 28 Sep 2026; scoped 2 Nov 2026). Pure: shared by the client's questionnaire, the console and the daily sender.
 *
 * The client lists their vendors in this order. Venue is required (contact name, email and phone); every other vendor
 * is optional, but one that is started needs all three. Weddings only: bridal, engagement and anniversary sessions
 * never see the section.
 *
 * Follow-ups go to every vendor with contact details 3 days, 30 days and 6 months after the event, by email and text.
 * The wording is in Business Controls → Notifications → Vendors. Replies go to the business email and phone that
 * GoHighLevel reads, so sales can answer there; until those are set in Business Controls nothing is sent.
 */

export const VENDOR_TYPES = [
  { type: 'venue', label: 'Venue', required: true },
  { type: 'planner', label: 'Planner' },
  { type: 'musician_dj', label: 'Musician/DJ' },
  { type: 'florist', label: 'Florist' },
  { type: 'caterer', label: 'Caterer' },
  { type: 'officiant', label: 'Officiant' }
]

export const FOLLOW_UPS = [
  { step: '3d', label: '3 days after', days: 3 },
  { step: '30d', label: '30 days after', days: 30 },
  { step: '6m', label: '6 months after', months: 6 }
]

/** Bridal, engagement and anniversary sessions have no vendor section. */
export function isSessionBooking(booking) {
  const b = booking || {}
  const p = b.personalization_data || {}
  const kinds = [b.package_type, b.event_type, p.session_type, p.package?.packageType, p.gift_session?.type].map(v => String(v || '').toLowerCase())
  return kinds.some(k => /engagement|bridal|anniversary|_session$/.test(k))
}

const clean = v => String(v ?? '').trim()
const digits = v => clean(v).replace(/\D/g, '')
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** One row per vendor type, in order, with whatever the client saved (answers.vendors, an array). */
export function vendorRows(saved) {
  const list = Array.isArray(saved) ? saved : []
  return VENDOR_TYPES.map(t => {
    const s = list.find(v => v && v.type === t.type) || {}
    return { type: t.type, label: t.label, required: Boolean(t.required), contact_name: clean(s.contact_name), email: clean(s.email), phone: clean(s.phone) }
  })
}

const started = v => Boolean(v.contact_name || v.email || v.phone)

/**
 * Problems per vendor type ({ venue: ['Email'], ... }); empty when the section is complete.
 * A phone is 10 digits (11 starting with 1 is fine).
 */
export function vendorErrors(rows) {
  const out = {}
  for (const v of rows || []) {
    if (!v.required && !started(v)) continue
    const missing = []
    if (!v.contact_name) missing.push('Contact name')
    if (!EMAIL_RE.test(v.email)) missing.push('Email')
    const d = digits(v.phone)
    if (!(d.length === 10 || (d.length === 11 && d[0] === '1'))) missing.push('Phone number')
    if (missing.length) out[v.type] = missing
  }
  return out
}

/** What is stored in answers.vendors: only the vendors the client filled in. */
export function vendorsToSave(rows) {
  return (rows || []).filter(started).map(v => ({ type: v.type, contact_name: v.contact_name, email: v.email.toLowerCase(), phone: v.phone }))
}

const dateOnly = v => String(v || '').slice(0, 10)
const noon = iso => new Date(`${iso}T12:00:00Z`)

/** The day each follow-up is due for an event date (YYYY-MM-DD), or null without a date. */
export function followUpDate(eventDate, step) {
  const d = dateOnly(eventDate)
  const f = FOLLOW_UPS.find(x => x.step === step)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !f) return null
  const t = noon(d)
  if (f.days) t.setUTCDate(t.getUTCDate() + f.days)
  if (f.months) {
    const day = t.getUTCDate()
    t.setUTCDate(1)
    t.setUTCMonth(t.getUTCMonth() + f.months)
    const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate()
    t.setUTCDate(Math.min(day, last))
  }
  return t.toISOString().slice(0, 10)
}

/** "Ana & Ben" from the questionnaire's names; falls back to the client's name. */
export function coupleName(answers, fallback = '') {
  const a = answers || {}
  const one = clean(a.person1_first_name)
  const two = clean(a.person2_first_name)
  if (one && two) return `${one} & ${two}`
  return one || two || clean(fallback) || 'the couple'
}

/**
 * The follow-ups to send today: for each booking with vendors, each step that is due (on or before today, and at
 * most 14 days late so turning sending on never floods old weddings), each vendor and each channel not already sent.
 * @param p.today     YYYY-MM-DD
 * @param p.bookings  [{ id, event_date, booking_status, is_test, customer_name, answers }]
 * @param p.sent      Set of `${booking_id}:${vendor_type}:${step}:${channel}` already sent
 * @param p.channels  ['email', 'sms'] that may send now (each needs its reply address set)
 */
export const LATE_LIMIT_DAYS = 14
export function dueFollowUps({ today, bookings = [], sent = new Set(), channels = ['email', 'sms'] }) {
  const out = []
  const daysBetween = (a, b) => Math.round((noon(b) - noon(a)) / 86400000)
  for (const b of bookings) {
    if (b.is_test || String(b.booking_status || '').toLowerCase().startsWith('cancel')) continue
    const vendors = vendorsToSave(vendorRows(b.answers?.vendors))
    if (!vendors.length) continue
    for (const f of FOLLOW_UPS) {
      const due = followUpDate(b.event_date, f.step)
      if (!due || due > today || daysBetween(due, today) > LATE_LIMIT_DAYS) continue
      for (const v of vendors) {
        for (const channel of channels) {
          const key = `${b.id}:${v.type}:${f.step}:${channel}`
          if (sent.has(key)) continue
          out.push({ key, booking_id: b.id, vendor_type: v.type, step: f.step, channel, to: channel === 'email' ? v.email : v.phone, values: { vendor_name: v.contact_name, couple_name: coupleName(b.answers, b.customer_name), event_date: eventWords(b.event_date) } })
        }
      }
    }
  }
  return out
}

/** "October 4, 2026". */
export function eventWords(iso) {
  const d = dateOnly(iso)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return 'the wedding'
  return noon(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}
